import type { ChangeEvent, CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { OrdbokEntry } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { scrollToTarget } from '@/lib/motion/engine'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { ClosingCta } from '@/components/generals/closing-cta'
import { ORDBOK } from '@/constants/ordbok'

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ'.split('')

const normalize = (value: string): string => value.toLocaleLowerCase('nb')

const letterOf = (term: string): string => {
    const first = term.trim().charAt(0).toLocaleUpperCase('nb')

    return ALPHABET.includes(first) ? first : '#'
}

type Term = Pick<OrdbokEntry, 'slug' | 'term'>

interface OrdbokenProps {
    entries: Term[]
}

const Ordboken: FC<OrdbokenProps> = ({ entries }): ReactNode => {
    const [search, setSearch] = useState('')
    const needle = normalize(search.trim())

    const groups = useMemo(() => {
        const matches = needle ? entries.filter(entry => normalize(entry.term).includes(needle)) : entries
        const map = new Map<string, Term[]>()

        matches.forEach(entry => {
            const letter = letterOf(entry.term)
            const list = map.get(letter) ?? []

            list.push(entry)
            map.set(letter, list)
        })

        return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, 'nb'))
    }, [needle, entries])

    const count = groups.reduce((sum, [, list]) => sum + list.length, 0)
    const present = new Set(groups.map(([letter]) => letter))

    const jump = (letter: string): void => {
        const target = document.getElementById(`bokstav-${letter}`)

        if (target) scrollToTarget(target, -150)
    }

    return (
        <Layout header='light'>
            <Seo
                title='Håndverkerordboken'
                description={`Håndverkerordboken: ${entries.length} ord og forkortelser fra byggeplassen forklart på godt norsk, fra HMS og SJA til TEK17, tiltaksklasse og ansvarsrett.`}
            />

            <section id='ordboken' className='tone-butter relative overflow-hidden' data-header='light'>
                <div className='grid-lines' />

                <div className='wrap relative pt-[calc(var(--header-h)+5rem)] pb-16 md:pb-20'>
                    <p className='t-label load-fade text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>
                        Håndverkerordboken · <span className='tabular'>{entries.length}</span> oppslag
                    </p>

                    <h1 className='t-mega mt-8'>
                        <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                            <SplitWords text='Ord fra' />
                        </span>
                        <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.3s' } as CSSProperties}>
                            <SplitWords text='byggeplassen.' start={2} />
                        </span>
                    </h1>

                    <div className='load-fade mt-12 grid grid-cols-12 gap-x-6 gap-y-8' style={{ '--load-delay': '0.7s' } as CSSProperties}>
                        <p className='t-lead col-span-12 max-w-lg text-tone-muted pretty md:col-span-5'>
                            Forkortelser, forskrifter og fagord forklart på godt norsk. Fra HMS og SJA til TEK17 og ansvarsrett.
                        </p>

                        <div className='col-span-12 md:col-span-6 md:col-start-7'>
                            <label htmlFor='ordbok-sok' className='t-label text-tone-muted'>Søk i ordboken</label>
                            <div className='relative mt-3'>
                                <Search className='pointer-events-none absolute left-0 top-1/2 size-6 -translate-y-1/2' aria-hidden='true' />
                                <input
                                    id='ordbok-sok'
                                    type='search'
                                    value={search}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                                    placeholder='For eksempel SJA'
                                    autoComplete='off'
                                    className='ordbok-search w-full'
                                />
                                {search && (
                                    <button type='button' onClick={() => setSearch('')} className='absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center' aria-label='Tøm søket'>
                                        <X className='size-5' aria-hidden='true' />
                                    </button>
                                )}
                            </div>
                            <p className='t-label mt-3 tabular text-tone-muted' aria-live='polite'>
                                {needle ? `${count} treff` : `${entries.length} oppslag`}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <nav aria-label='Hopp til bokstav' className='tone-paper sticky top-[var(--header-h)] z-20 border-y border-tone-line' data-header='light'>
                <div className='wrap no-scrollbar flex overflow-x-auto' data-lenis-prevent-horizontal>
                    {ALPHABET.map(letter => (
                        <button
                            key={letter}
                            type='button'
                            onClick={() => jump(letter)}
                            disabled={!present.has(letter)}
                            className='ordbok-letter'
                            aria-label={`Hopp til ${letter}`}
                        >
                            {letter}
                        </button>
                    ))}
                </div>
            </nav>

            <section id='oppslag' className='tone-paper relative py-16 md:py-24' data-header='light'>
                <div className='wrap'>
                    {groups.length === 0 && (
                        <div className='py-16'>
                            <p className='t-h2 balance'>Ingen ord passer «{search}».</p>
                            <p className='mt-4 max-w-md text-tone-muted'>Prøv et kortere ord, eller en forkortelse. Savner du et ord, kan du gjerne si fra til oss.</p>
                            <Link href='/kontakt' className='u-link-static mt-6 inline-block font-semibold'>Foreslå et ord</Link>
                        </div>
                    )}

                    {groups.map(([letter, list]) => (
                        <div key={letter} id={`bokstav-${letter}`} className='grid grid-cols-12 gap-x-6 border-t border-tone-fg pt-6 pb-14 md:pb-20'>
                            <p className='ordbok-glyph col-span-12 md:col-span-3' aria-hidden='true'>{letter}</p>
                            <h2 className='sr-only'>{letter}</h2>
                            <ul className={cn('col-span-12 grid grid-cols-1 gap-x-6 sm:grid-cols-2 md:col-span-9', list.length > 6 && 'lg:grid-cols-3')}>
                                {list.map(entry => (
                                    <li key={entry.slug} className='border-b border-tone-line'>
                                        <Link href={`/haandverkerordboken/${entry.slug}`} className='ordbok-term group flex items-baseline justify-between gap-3 py-3'>
                                            <span>{entry.term}</span>
                                            <span className='ordbok-term-arrow t-label text-tone-ink' aria-hidden='true'>Les</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <ClosingCta
                tone='night'
                eyebrow='Fra ord til handling'
                lines={['Mindre papir.', 'Mer håndverk.']}
                text='Svenn tar seg av KS, HMS, timer og tilbud, så du slipper å slå opp alt selv. Prøv gratis i 14 dager.'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<OrdbokenProps> = async () => ({
    props: {
        entries: [...ORDBOK]
            .sort((a, b) => a.term.localeCompare(b.term, 'nb'))
            .map(entry => ({ slug: entry.slug, term: entry.term }))
    }
})

export default Ordboken
